// src/pages/TrackingPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { trackShipment } from '../utils/shiprocket';
import { Package, Truck, CheckCircle, Clock, MapPin, ArrowLeft, Phone } from 'lucide-react';

const STEPS = [
  { key: 'placed',    label: 'Order Placed',      Icon: CheckCircle },
  { key: 'confirmed', label: 'Confirmed',          Icon: Clock       },
  { key: 'shipped',   label: 'Shipped',            Icon: Package     },
  { key: 'transit',   label: 'Out for Delivery',   Icon: Truck       },
  { key: 'delivered', label: 'Delivered',          Icon: CheckCircle },
];

function getStepIndex(status = '') {
  const s = status.toUpperCase();
  if (s.includes('DELIVERED'))                         return 4;
  if (s.includes('OUT') || s.includes('TRANSIT'))      return 3;
  if (s.includes('SHIP') || s.includes('PICKUP'))      return 2;
  if (s.includes('PROCESS') || s.includes('CONFIRM'))  return 1;
  return 0;
}

export default function TrackingPage() {
  const { orderId }              = useParams();
  const [order,    setOrder]     = useState(null);
  const [tracking, setTracking]  = useState(null);
  const [loading,  setLoading]   = useState(true);
  const [error,    setError]     = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const snap = await getDoc(doc(db, 'orders', orderId));
        if (!snap.exists()) { setError('Order not found'); setLoading(false); return; }
        const orderData = { id: snap.id, ...snap.data() };
        setOrder(orderData);

        if (orderData.awbCode) {
          try {
            const trackData = await trackShipment(orderData.awbCode);
            setTracking(trackData?.tracking_data || null);
          } catch { /* tracking optional */ }
        }
      } catch {
        setError('Could not load order details.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orderId]);

  if (loading) return (
    <div className="min-h-screen bg-cream pt-20 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-rose-100 border-t-rose-500 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-400 font-body animate-pulse">Loading your order...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-cream pt-20 flex items-center justify-center">
      <div className="text-center">
        <div className="text-6xl mb-4">😕</div>
        <h2 className="font-display font-bold text-2xl text-gray-800 mb-2">{error}</h2>
        <Link to="/orders" className="text-rose-500 font-body hover:underline">← Back to Orders</Link>
      </div>
    </div>
  );

  const currentStatus = tracking?.shipment_track?.[0]?.current_status || order?.trackingStatus || 'Order Placed';
  const currentStep   = getStepIndex(currentStatus);
  const trackHistory  = tracking?.shipment_track_activities || [];
  const estimatedDate = tracking?.shipment_track?.[0]?.edd   || null;
  const orderDate     = order?.createdAt?.toDate?.()?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) || 'Recently';

  return (
    <div className="min-h-screen bg-cream pt-20 pb-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">

        {/* Header */}
        <div className="py-8">
          <Link to="/orders"
            className="inline-flex items-center gap-1.5 text-gray-400 hover:text-rose-500 text-sm font-body transition-colors mb-5">
            <ArrowLeft size={16} /> Back to Orders
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display font-bold text-3xl text-gray-900">Track Order</h1>
              <p className="text-gray-400 font-body text-sm mt-1">
                #{orderId.slice(-8).toUpperCase()} · Placed on {orderDate}
              </p>
            </div>
            {order?.courierName && (
              <div className="text-right">
                <p className="text-xs text-gray-400 font-body">via</p>
                <p className="font-body font-bold text-gray-800">{order.courierName}</p>
              </div>
            )}
          </div>
        </div>

        {/* Main Status Card */}
        <div className="bg-white rounded-3xl p-6 shadow-soft mb-4">
          {/* Current status banner */}
          <div className={`rounded-2xl p-4 mb-6 flex items-center justify-between ${
            currentStep === 4 ? 'bg-green-50 border border-green-200' : 'bg-rose-50 border border-rose-200'
          }`}>
            <div>
              <p className="text-xs font-body font-bold uppercase tracking-widest text-gray-400 mb-0.5">Current Status</p>
              <p className={`font-display font-bold text-xl ${currentStep === 4 ? 'text-green-700' : 'text-rose-600'}`}>
                {currentStatus}
              </p>
              {estimatedDate && currentStep < 4 && (
                <p className="text-xs text-gray-500 font-body mt-1">
                  Expected by {new Date(estimatedDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long' })}
                </p>
              )}
            </div>
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
              currentStep === 4 ? 'bg-green-100' : 'bg-rose-100'
            }`}>
              {currentStep === 4
                ? <CheckCircle size={28} className="text-green-500" />
                : <Truck size={28} className="text-rose-500" />
              }
            </div>
          </div>

          {/* Step Progress */}
          <div className="relative px-2">
            {/* Background line */}
            <div className="absolute top-5 left-8 right-8 h-0.5 bg-gray-100 z-0" />
            {/* Progress line */}
            <div className="absolute top-5 left-8 h-0.5 bg-rose-500 z-0 transition-all duration-1000"
              style={{ width: `calc(${(currentStep / (STEPS.length - 1)) * 100}% - ${currentStep === 0 ? 100 : currentStep === STEPS.length - 1 ? 0 : 16}px)` }} />

            <div className="relative z-10 flex justify-between">
              {STEPS.map((step, i) => {
                const done   = i < currentStep;
                const active = i === currentStep;
                const { Icon } = step;
                return (
                  <div key={step.key} className="flex flex-col items-center gap-2.5">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 ${
                      done   ? 'bg-rose-500 text-white'
                      : active ? 'bg-rose-500 text-white ring-4 ring-rose-100'
                      : 'bg-gray-100 text-gray-400'
                    }`}>
                      {done ? <CheckCircle size={18} /> : <Icon size={18} />}
                    </div>
                    <span className={`text-[10px] font-body font-semibold text-center leading-tight max-w-[60px] ${
                      done || active ? 'text-rose-500' : 'text-gray-400'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* AWB / Courier Info */}
        {order?.awbCode && (
          <div className="bg-white rounded-3xl p-5 shadow-soft mb-4">
            <h3 className="font-body font-semibold text-gray-700 text-sm mb-3">Shipment Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-2xl p-3">
                <p className="text-xs text-gray-400 font-body">AWB Number</p>
                <p className="font-body font-bold text-gray-800 text-sm mt-0.5">{order.awbCode}</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-3">
                <p className="text-xs text-gray-400 font-body">Courier Partner</p>
                <p className="font-body font-bold text-gray-800 text-sm mt-0.5">{order.courierName || '—'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Live Activity Timeline */}
        {trackHistory.length > 0 && (
          <div className="bg-white rounded-3xl p-6 shadow-soft mb-4">
            <h3 className="font-display font-semibold text-gray-900 mb-5 flex items-center gap-2">
              <MapPin size={18} className="text-rose-400" /> Tracking Activity
            </h3>
            <div className="space-y-0">
              {trackHistory.slice(0, 10).map((activity, i) => (
                <div key={i} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-3 h-3 rounded-full mt-1 shrink-0 ${i === 0 ? 'bg-rose-500 ring-4 ring-rose-100' : 'bg-gray-200'}`} />
                    {i < Math.min(trackHistory.length, 10) - 1 && (
                      <div className="w-0.5 flex-1 bg-gray-100 my-1 min-h-[24px]" />
                    )}
                  </div>
                  <div className="pb-5">
                    <p className={`font-body font-semibold text-sm ${i === 0 ? 'text-rose-600' : 'text-gray-700'}`}>
                      {activity['sr-status-label'] || activity.status || 'Update'}
                    </p>
                    {activity.location && (
                      <p className="text-gray-400 font-body text-xs mt-0.5 flex items-center gap-1">
                        <MapPin size={10} /> {activity.location}
                      </p>
                    )}
                    <p className="text-gray-300 font-body text-xs mt-0.5">{activity.date || ''}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No AWB yet */}
        {!order?.awbCode && (
          <div className="bg-amber-50 border border-amber-200 rounded-3xl p-5 mb-4">
            <p className="font-body font-semibold text-amber-700 text-sm mb-1">📦 Preparing Your Order</p>
            <p className="text-amber-600 font-body text-xs leading-relaxed">
              Your order is confirmed and being lovingly packed. Tracking will be live once your order is shipped — usually within 24–48 hours.
            </p>
          </div>
        )}

        {/* Order Items */}
        <div className="bg-white rounded-3xl p-6 shadow-soft mb-4">
          <h3 className="font-display font-semibold text-gray-900 mb-4">Items in this Order</h3>
          <div className="space-y-3">
            {order?.items?.map((item, i) => (
              <div key={i} className="flex gap-3 items-center">
                <img src={item.image} alt={item.name}
                  className="w-16 h-16 rounded-2xl object-cover shrink-0 border border-rose-100" />
                <div className="flex-1 min-w-0">
                  <p className="font-body font-semibold text-gray-900 text-sm line-clamp-1">{item.name}</p>
                  <p className="text-xs text-gray-400 font-body mt-0.5">Quantity: {item.quantity}</p>
                </div>
                <p className="font-display font-bold text-gray-900 text-sm">
                  ₹{(item.price * item.quantity).toLocaleString('en-IN')}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
            <span className="text-gray-400 font-body text-sm">Total Paid</span>
            <span className="font-display font-bold text-xl text-gray-900">
              ₹{order?.total?.toLocaleString('en-IN')}
            </span>
          </div>
        </div>

        {/* Delivery Address */}
        {order?.shippingAddress && (
          <div className="bg-white rounded-3xl p-5 shadow-soft mb-4">
            <h3 className="font-body font-semibold text-gray-700 text-sm mb-3 flex items-center gap-2">
              <MapPin size={16} className="text-rose-400" /> Delivering To
            </h3>
            <p className="font-body font-semibold text-gray-900 text-sm">{order.shippingAddress.name}</p>
            <p className="font-body text-gray-500 text-xs mt-1 leading-relaxed">
              {order.shippingAddress.address}, {order.shippingAddress.city},{' '}
              {order.shippingAddress.state} — {order.shippingAddress.pincode}
            </p>
            <p className="font-body text-gray-400 text-xs mt-0.5">{order.shippingAddress.phone}</p>
          </div>
        )}

        {/* Help */}
        <div className="bg-gray-900 rounded-3xl p-6 text-white text-center">
          <div className="text-3xl mb-2">💬</div>
          <h3 className="font-display font-bold text-lg mb-1">Need Help?</h3>
          <p className="text-gray-400 font-body text-sm mb-4">Our team is available Mon–Sat, 10 AM – 7 PM</p>
          <a href="https://wa.me/918840075759"
            className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-2xl font-body font-semibold text-sm transition-colors">
            <Phone size={16} /> WhatsApp Support
          </a>
          <p className="text-gray-600 font-body text-xs mt-3">
            Please share your Order ID: <span className="font-mono text-gray-500">#{orderId.slice(-8).toUpperCase()}</span>
          </p>
        </div>

      </div>
    </div>
  );
}
